import { config } from "../models/config";
import { authHeaders, jsonHeaders } from "./apiHeaders";
import type { GetToken } from "./accountApi";
import { ApiError, friendlyMessage } from "./accountApi";
import type { AiSummaryResult, AiTranslation } from "../models/ai";
import type {
  Attachment,
  AttachmentOwnerType,
  AuditEvent,
  DocComment,
  CreateDocTagRequest,
  CreateDocTypeRequest,
  CreateDocumentRequest,
  CreateExternalDocumentRequest,
  CreateTraceLinkRequest,
  DocReviewTask,
  DocTag,
  DocType,
  Document,
  DocumentRevision,
  ExternalDocRevision,
  ExternalDocument,
  RevisionDiff,
  PlanVersionRequest,
  RequestReviewRequest,
  CreateTestCaseRequest,
  SaveStepsRequest,
  TestCase,
  TraceEndpointType,
  TraceLink,
  UpdateDocumentRequest,
  UpdateTestCaseRequest,
  UpdateVersionRequest,
  UploadRevisionRequest,
  Version,
  WorkAreaType,
} from "../models/eqms";

export type { GetToken };
export { ApiError, friendlyMessage };

// Typed client for the eQMS REST surface. Same bearer-fetch pattern as projectApi.ts.
async function call<T>(getToken: GetToken, method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${config.apiBase}${path}`, {
    method,
    headers: jsonHeaders(getToken),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json().catch(() => undefined as T);
}

const enc = encodeURIComponent;

// ---- Versions ----
export const listVersions = (t: GetToken, projectId: string) =>
  call<Version[]>(t, "GET", `/api/projects/${projectId}/versions`);
export const planVersion = (t: GetToken, projectId: string, req: PlanVersionRequest) =>
  call<Version>(t, "POST", `/api/projects/${projectId}/versions`, req);
export const getVersion = (t: GetToken, id: string) => call<Version>(t, "GET", `/api/versions/${id}`);
export const updateVersion = (t: GetToken, id: string, req: UpdateVersionRequest) =>
  call<Version>(t, "PATCH", `/api/versions/${id}`, req);
export const startVersionDev = (t: GetToken, id: string) =>
  call<Version>(t, "POST", `/api/versions/${id}/start-dev`);
export const releaseVersion = (t: GetToken, id: string, sprintId?: string | null) =>
  call<Version>(t, "POST", `/api/versions/${id}/release`, { sprintId: sprintId ?? null });
export const supersedeVersion = (t: GetToken, id: string) =>
  call<Version>(t, "POST", `/api/versions/${id}/supersede`);
export const deleteVersion = (t: GetToken, id: string) =>
  call<void>(t, "DELETE", `/api/versions/${id}`);

// ---- Documents ----
export const listDocuments = (t: GetToken, projectId: string, workArea?: WorkAreaType) =>
  call<Document[]>(t, "GET",
    `/api/projects/${projectId}/documents${workArea ? `?workArea=${enc(workArea)}` : ""}`);
export const createDocument = (t: GetToken, projectId: string, req: CreateDocumentRequest) =>
  call<Document>(t, "POST", `/api/projects/${projectId}/documents`, req);
export const getDocument = (t: GetToken, id: string) => call<Document>(t, "GET", `/api/documents/${id}`);
export const updateDocument = (t: GetToken, id: string, req: UpdateDocumentRequest) =>
  call<Document>(t, "PATCH", `/api/documents/${id}`, req);
export const deleteDocument = (t: GetToken, id: string) =>
  call<void>(t, "DELETE", `/api/documents/${id}`);

export const listRevisions = (t: GetToken, docId: string) =>
  call<DocumentRevision[]>(t, "GET", `/api/documents/${docId}/revisions`);
export const uploadRevision = (t: GetToken, docId: string, req: UploadRevisionRequest) =>
  call<Document>(t, "POST", `/api/documents/${docId}/revisions`, req);

export const listReviewTasks = (t: GetToken, docId: string) =>
  call<DocReviewTask[]>(t, "GET", `/api/documents/${docId}/review-tasks`);
export const requestReview = (t: GetToken, docId: string, req: RequestReviewRequest) =>
  call<Document>(t, "POST", `/api/documents/${docId}/review`, req);
export const actOnReview = (t: GetToken, taskId: string, action: "approve" | "reject", comment?: string) =>
  call<Document>(t, "POST", `/api/documents/review-tasks/${taskId}`, { action, comment: comment ?? "" });
export const releaseDocument = (t: GetToken, docId: string, versionId?: string | null) =>
  call<Document>(t, "POST", `/api/documents/${docId}/release`, { versionId: versionId ?? null });
export const startDocumentRevision = (t: GetToken, docId: string) =>
  call<Document>(t, "POST", `/api/documents/${docId}/start-revision`);
export const obsoleteDocument = (t: GetToken, docId: string) =>
  call<Document>(t, "POST", `/api/documents/${docId}/obsolete`);
export const setDocumentTags = (t: GetToken, docId: string, tagIds: string[]) =>
  call<Document>(t, "PUT", `/api/documents/${docId}/tags`, { tagIds });
export const setDocumentDerivedFrom = (t: GetToken, docId: string, issueIds: string[]) =>
  call<Document>(t, "PUT", `/api/documents/${docId}/derived-from`, { issueIds });
export const setDocumentAiSummary = (t: GetToken, docId: string, summary: string, keyPoints: string[]) =>
  call<Document>(t, "PUT", `/api/documents/${docId}/ai-summary`, { summary, keyPoints });
export const myReviewInbox = (t: GetToken) =>
  call<DocReviewTask[]>(t, "GET", `/api/me/document-review-inbox`);

// ---- Document audit trail + comments ----
export const listDocAudit = (t: GetToken, docId: string) =>
  call<AuditEvent[]>(t, "GET", `/api/documents/${docId}/audit`);
export const listDocComments = (t: GetToken, docId: string) =>
  call<DocComment[]>(t, "GET", `/api/documents/${docId}/comments`);
export const addDocComment = (t: GetToken, docId: string, body: string) =>
  call<DocComment>(t, "POST", `/api/documents/${docId}/comments`, { body });

// ---- Test cases (design/test lane) ----
export const listTestCases = (
  t: GetToken, projectId: string,
  filters?: { workArea?: string; status?: string; q?: string },
) => {
  const p = new URLSearchParams();
  if (filters?.workArea) p.set("workArea", filters.workArea);
  if (filters?.status) p.set("status", filters.status);
  if (filters?.q) p.set("q", filters.q);
  const qs = p.toString();
  return call<TestCase[]>(t, "GET", `/api/projects/${projectId}/test-cases${qs ? `?${qs}` : ""}`);
};
export const getTestCase = (t: GetToken, id: string) =>
  call<TestCase>(t, "GET", `/api/test-cases/${id}`);
export const createTestCase = (t: GetToken, projectId: string, req: CreateTestCaseRequest) =>
  call<TestCase>(t, "POST", `/api/projects/${projectId}/test-cases`, req);
export const updateTestCase = (t: GetToken, id: string, req: UpdateTestCaseRequest) =>
  call<TestCase>(t, "PATCH", `/api/test-cases/${id}`, req);
export const changeTestCaseStatus = (t: GetToken, id: string, status: string) =>
  call<TestCase>(t, "POST", `/api/test-cases/${id}/status`, { status });
export const saveTestSteps = (t: GetToken, id: string, req: SaveStepsRequest) =>
  call<TestCase>(t, "PUT", `/api/test-cases/${id}/steps`, req);
export const deleteTestCase = (t: GetToken, id: string) =>
  call<void>(t, "DELETE", `/api/test-cases/${id}`);

// ---- AI document summary generation + translation ----
export const generateDocAiSummary = (t: GetToken, docId: string) =>
  call<AiSummaryResult>(t, "POST", `/api/documents/${docId}/ai-summary/generate`);
/** Translates a document's own text field (title|summary|aiSummary|memo) — lazy, DB-cached. */
export const translateDocField = (t: GetToken, docId: string, field: string, targetLang = "en") =>
  call<AiTranslation>(t, "POST", `/api/documents/${docId}/translate`, { field, targetLang });

// ---- Traceability ----
export const listTraceLinks = (t: GetToken, projectId: string) =>
  call<TraceLink[]>(t, "GET", `/api/projects/${projectId}/trace-links`);
export const createTraceLink = (t: GetToken, projectId: string, req: CreateTraceLinkRequest) =>
  call<TraceLink>(t, "POST", `/api/projects/${projectId}/trace-links`, req);
export const deleteTraceLink = (t: GetToken, id: string) =>
  call<void>(t, "DELETE", `/api/trace-links/${id}`);
export const listTraceByEndpoint = (t: GetToken, projectId: string, type: TraceEndpointType, id: string) =>
  call<TraceLink[]>(t, "GET",
    `/api/projects/${projectId}/trace-endpoints?type=${enc(type)}&id=${enc(id)}`);

// ---- Doc types / tags ----
export const listDocTypes = (t: GetToken, workspaceId: string) =>
  call<DocType[]>(t, "GET", `/api/workspaces/${workspaceId}/doc-types`);
export const createDocType = (t: GetToken, workspaceId: string, req: CreateDocTypeRequest) =>
  call<DocType>(t, "POST", `/api/workspaces/${workspaceId}/doc-types`, req);
export const deleteDocType = (t: GetToken, id: string) => call<void>(t, "DELETE", `/api/doc-types/${id}`);

export const listDocTags = (t: GetToken, workspaceId: string) =>
  call<DocTag[]>(t, "GET", `/api/workspaces/${workspaceId}/doc-tags`);
export const createDocTag = (t: GetToken, workspaceId: string, req: CreateDocTagRequest) =>
  call<DocTag>(t, "POST", `/api/workspaces/${workspaceId}/doc-tags`, req);
export const deleteDocTag = (t: GetToken, id: string) => call<void>(t, "DELETE", `/api/doc-tags/${id}`);

// ---- External documents ----
export const listExternalDocuments = (t: GetToken, workspaceId: string) =>
  call<ExternalDocument[]>(t, "GET", `/api/workspaces/${workspaceId}/external-documents`);
export const createExternalDocument = (t: GetToken, workspaceId: string, req: CreateExternalDocumentRequest) =>
  call<ExternalDocument>(t, "POST", `/api/workspaces/${workspaceId}/external-documents`, req);
export const listExternalDocRevisions = (t: GetToken, id: string) =>
  call<ExternalDocRevision[]>(t, "GET", `/api/external-documents/${enc(id)}/revisions`);

/** `toRevId` is the newer side, `fromRevId` the older one. */
export const diffExternalDocRevisions = (t: GetToken, id: string, fromRevId: string, toRevId: string) =>
  call<RevisionDiff>(t, "GET",
    `/api/external-documents/${enc(id)}/revisions/${enc(toRevId)}/diff?against=${enc(fromRevId)}`);

/** Uploads the file as a new revision; returns the document now pointing at it. */
export async function uploadExternalDocFile(
  getToken: GetToken, id: string, file: File, changeSummary?: string,
): Promise<ExternalDocument> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(
    `${config.apiBase}/api/external-documents/${enc(id)}/file${
      changeSummary ? `?changeSummary=${enc(changeSummary)}` : ""}`,
    { method: "POST", headers: authHeaders(getToken), body: form },
  );
  if (!res.ok) {
    throw new ApiError(res.status, await res.json().catch(() => ({})));
  }
  return res.json();
}

/** Streams a revision's bytes for in-browser preview (the download variant triggers a save instead). */
export async function fetchExternalDocRevisionBytes(
  getToken: GetToken, id: string, revId: string,
): Promise<ArrayBuffer> {
  const res = await fetch(
    `${config.apiBase}/api/external-documents/${enc(id)}/revisions/${enc(revId)}/content`,
    { headers: authHeaders(getToken) },
  );
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  return res.arrayBuffer();
}

export const downloadExternalDocRevision = (t: GetToken, id: string, revId: string, filename: string) =>
  downloadBlob(t, `/api/external-documents/${enc(id)}/revisions/${enc(revId)}/content`, filename);

export const deleteExternalDocument = (t: GetToken, id: string) =>
  call<void>(t, "DELETE", `/api/external-documents/${id}`);

// ---- Attachments ----
export const listAttachments = (t: GetToken, ownerType: AttachmentOwnerType, ownerId: string) =>
  call<Attachment[]>(t, "GET", `/api/attachments?ownerType=${enc(ownerType)}&ownerId=${enc(ownerId)}`);
export const deleteAttachment = (t: GetToken, id: string) =>
  call<void>(t, "DELETE", `/api/attachments/${id}`);

// ---- File upload / download (multipart + authed blob stream via the same-origin BFF) ----
export interface DeriveRequest {
  workAreaType: string;
  docTypeId?: string | null;
  title: string;
  format?: string | null;
  issueIds: string[];
}

async function postFile<T>(getToken: GetToken, path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${config.apiBase}${path}`, {
    method: "POST",
    headers: authHeaders(getToken),
    body: form,
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

/** Downloads a stored object through the BFF (bearer-authed) and triggers a browser save. */
async function downloadBlob(getToken: GetToken, path: string, filename: string): Promise<void> {
  const res = await fetch(`${config.apiBase}${path}`, { headers: authHeaders(getToken) });
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const uploadRevisionFile = (
  t: GetToken, docId: string, file: File, revLabel: string, changeSummary?: string,
) => postFile<Document>(
  t,
  `/api/documents/${docId}/revision-files?revLabel=${enc(revLabel)}${
    changeSummary ? `&changeSummary=${enc(changeSummary)}` : ""}`,
  file,
);

export const downloadRevision = (t: GetToken, docId: string, revId: string, filename: string) =>
  downloadBlob(t, `/api/documents/${docId}/revisions/${revId}/content`, filename);

/** Streams a revision's stored bytes (bearer-authed) for inline in-browser preview. */
export async function fetchRevisionBytes(
  getToken: GetToken, docId: string, revId: string,
): Promise<ArrayBuffer> {
  const res = await fetch(`${config.apiBase}/api/documents/${docId}/revisions/${revId}/content`, {
    headers: authHeaders(getToken),
  });
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  return res.arrayBuffer();
}

export const uploadAttachmentFile = (
  t: GetToken, ownerType: AttachmentOwnerType, ownerId: string, file: File,
) => postFile<Attachment>(
  t, `/api/attachments/upload?ownerType=${enc(ownerType)}&ownerId=${enc(ownerId)}`, file);

export const downloadAttachment = (t: GetToken, id: string, filename: string) =>
  downloadBlob(t, `/api/attachments/${id}/content`, filename);

/** Streams an attachment's stored bytes (bearer-authed) for inline in-browser preview. */
export async function fetchAttachmentBytes(getToken: GetToken, id: string): Promise<ArrayBuffer> {
  const res = await fetch(`${config.apiBase}/api/attachments/${id}/content`, {
    headers: authHeaders(getToken),
  });
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  return res.arrayBuffer();
}

export const deriveDocument = (t: GetToken, projectId: string, req: DeriveRequest) =>
  call<Document>(t, "POST", `/api/projects/${projectId}/documents/derive`, req);
